/* eslint-disable max-lines */
import React, { Fragment, lazy, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { isIOS, isMobile } from 'react-device-detect';
import { fromJS } from 'immutable';

import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import { translate } from 'services/i18nService';
import { USER_LOGGED_OUT_ERROR } from 'services/MessagingService/EventNames';
import createMessagingInstance from 'services/MessagingService/MessagingService';
import NotificationService from 'services/NotificationService';
import StreamingService from 'services/StreamingService';
import CashoutVerificationStore from 'stores/CashoutVerificationStore';
import ModalService from 'services/ModalService';
import { ACCOUNT_MENU, MOBILE_MENU } from 'pages/Drawer/Drawers';
import IdentitiesService from 'services/IdentitiesService';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import {
  DRAWER_MENU_ITEMS,
  MEDIA_BREAKPOINT_MOBILE_DOWN,
  MEDIA_BREAKPOINT_TABLET_DOWN,
} from 'enum/helpers';
import { CASHOUT_STATUS } from 'enum/profile';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { getMasterDomain } from 'utils/helpers';
import { hasUnapprovedSecondaryIdentity } from 'utils/identityHelpers';
import {
  isStudioAdminCheck,
  isStudioOwnerCheck,
} from 'utils/studio';
import useResize from 'hooks/useResize';
import { RegistrationModalCaller } from 'pages/RegistrationModal/utils';
import useFingerprintInfo from 'hooks/useFingerprintInfo';


const AccountMenuMobile = lazy(() => import('components/AccountMenuMobile/AccountMenuMobile'));
const DrawerContent = lazy(() => import('components/DrawerContent/DrawerContent'));
const LeftMobileMenu = lazy(() => import('components/LeftMobileMenu/LeftMobileMenu'));
const MobileMenu = lazy(() => import('components/MobileMenu/MobileMenu'));
const StudioAccountMenuMobile = lazy(() => import('components/StudioAccountMenuMobile/StudioAccountMenuMobile'));
const HeaderMain = lazy(() => import('./HeaderMain/HeaderMain'));

const Header = (props) => {
  const {
    user,
    history,
    userActions,
    appActions,
    dispatch,
    onGenderChange,
    setGuestBilllingModalProp,
    isPerformerHub,
    isLoginPage,
    showRegistrationModal,
    match,
    processedPath,
    showGuestBillingModal,
    drawer,
    availableLanguages,
    computedMatch,
    drawerActions,
    balance,
    onLoaded,
  } = props;
  const userId = user.user.getIn(['user', 'userId']);
  const fingerprintData = useFingerprintInfo(userId);

  const [width] = useResize();
  const isTabletViewActive = width <= MEDIA_BREAKPOINT_TABLET_DOWN;
  const isMobileViewActive = width <= MEDIA_BREAKPOINT_MOBILE_DOWN;

  const isStudioOwner = isStudioOwnerCheck(user);
  const isStudioAdmin = isStudioAdminCheck(user);
  const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const isLoggedIn = user.username !== null;

  const NO_OF_MILLISECONDS = 500;
  const messagingService = createMessagingInstance();

  const checkAndHandleGuest = useCallback(() => {
    if (isMobileViewActive) {
      drawerActions.close(MOBILE_MENU);
    }

    if (!isLoggedIn) {
      ModalService.showModal(ModalService.MODAL_KEYS.LOGIN_MODAL_V2,
        { handleOpenRegistrationModal: () => appActions.setShowRegistrationModal(true, RegistrationModalCaller.HEADER) }
      );

      return true;
    }

    if (isGuestBilling) {
      setGuestBilllingModalProp(true);

      return true;
    }

    return false;
  }, [
    appActions,
    drawerActions,
    isGuestBilling,
    isLoggedIn,
    isMobileViewActive,
    setGuestBilllingModalProp,
  ]);

  function setIsViewerBroadcastCookie() {
    CookieStorageService.set('isViewerBroadcast', 'true', {
      path: '/',
      domain: getMasterDomain(),
    });
  }

  const openBroadcastWindow = useCallback(() => {
    ModalService.hideModal();
    if (user.username) {
      userActions.getConsentStatus(user.username);
    }

    if (isStudioOwner) {
      return;
    }

    StreamingService.openBroadcastWindow();
  }, [
    isStudioOwner,
    user,
    userActions,
  ]);

  const openBroadcastConsentModal = useCallback(() => {
    const modalProps = { user };
    let modal = undefined;
    if (isMobileViewActive) {
      drawerActions.close(MOBILE_MENU);
      modal = ModalService.MODAL_KEYS.BROADCAST_MOBILE_CONSENT_MODAL;
    } else {
      modal = ModalService.MODAL_KEYS.BROADCASTING_CONSENT_MODAL;
      modalProps.showBroadcastingWindow = openBroadcastWindow;
    }
    ModalService.showModal(modal, modalProps);
  }, [
    drawerActions,
    isMobileViewActive,
    openBroadcastWindow,
    user,
  ]);

  const startBroadcast = useCallback(() => {
    StreamingService.openBroadcastWindow();
  }, []);

  function startBroadcastOnMobile() {
    if (isIOS) {
      window.location = routeDefinitions.webrtc;
    } else {
      window.location = routeDefinitions.appBroadcasting;
    }
  }

  const proceedToStartBroadcast = useCallback(() => {
    if (!user.broadcastingConsent) {
      openBroadcastConsentModal();

      return;
    }

    if (isMobile) {
      startBroadcastOnMobile();
    } else {
      startBroadcast();
    }
  }, [openBroadcastConsentModal, startBroadcast, user]);

  const openBasicRedirectOnboardingModal = useCallback(() => {
    ModalService.showModal(ModalService.MODAL_KEYS.BASIC_REDIRECT_ONBOARDING_MODAL, {
      show: true,
      close: ModalService.hideModal,
      history,
    });
  }, [history]);

  const handleViewerBroadcast = useCallback(() => {
    ModalService.hideModal();

    const { cashoutStatus: { needsId } } = CashoutVerificationStore;

    if (needsId) {
      openBasicRedirectOnboardingModal();

      return;
    }

    setIsViewerBroadcastCookie();
    proceedToStartBroadcast();
  }, [proceedToStartBroadcast, openBasicRedirectOnboardingModal]);

  const openRedirectToManageDocumentationModal = useCallback(() => {
    ModalService.hideModal();
    if (isMobileViewActive) {
      drawerActions.close(MOBILE_MENU);
    }
    openBasicRedirectOnboardingModal();
  }, [drawerActions, isMobileViewActive, openBasicRedirectOnboardingModal]);

  const openChooseBroadcastTypeModal = useCallback(() => {
    if (isMobileViewActive) {
      drawerActions.close(MOBILE_MENU);
    }

    ModalService.showModal(
      ModalService.MODAL_KEYS.BROADCAST_TYPE_ONBOARDING_MODAL,
      {
        show: true,
        close: ModalService.hideModal,
        handleViewer: handleViewerBroadcast,
        handleBroadcaster: openRedirectToManageDocumentationModal,
      }
    );
  }, [
    drawerActions,
    handleViewerBroadcast,
    isMobileViewActive,
    openRedirectToManageDocumentationModal,
  ]);

  function checkRemoveIsViewerBroadcastCookie() {
    if (CookieStorageService.get('isViewerBroadcast')) {
      CookieStorageService.remove('isViewerBroadcast', {
        domain: getMasterDomain(),
        path: '/',
      });
    }
  }

  const openInformPendingModal = useCallback(() => {
    if (isMobileViewActive) {
      drawerActions.close(MOBILE_MENU);
    }

    ModalService.showModal(
      ModalService.MODAL_KEYS.INFORM_PENDING_ONBOARDING_MODAL,
      {
        show: true,
        close: ModalService.hideModal,
        onGoLive: proceedToStartBroadcast,
      }
    );
  }, [drawerActions, isMobileViewActive, proceedToStartBroadcast]);

  const onBroadcastButtonClick = useCallback(async() => {
    if (checkAndHandleGuest()) {
      CookieStorageService.set('Cam4_Models', true);

      return;
    }

    const { username } = user;
    await CashoutVerificationStore.getCashoutStatusAsync(username);

    checkRemoveIsViewerBroadcastCookie();
    const { cashoutStatus: { paypalStatus } } = CashoutVerificationStore;
    if (paypalStatus === CASHOUT_STATUS.NONE) {
      openChooseBroadcastTypeModal();

      return;
    }

    if (paypalStatus === CASHOUT_STATUS.BROADCAST_APPROVED) {
      const broadcastWhileMainIdPendingCF = ControlledFeatureService.setUser(user).has(
        allUserGroups.BROADCAST_WHILE_MAIN_ID_PENDING
      );
      if (!broadcastWhileMainIdPendingCF) {
        openInformPendingModal();

        return;
      }

      const data = await IdentitiesService.getIdentities(username);
      const identities = fromJS(data);
      if (identities !== undefined && hasUnapprovedSecondaryIdentity(identities)) {
        openInformPendingModal();

        return;
      }
    }

    proceedToStartBroadcast();
  }, [
    checkAndHandleGuest,
    openChooseBroadcastTypeModal,
    openInformPendingModal,
    proceedToStartBroadcast,
    user,
  ]);

  function handleLogOutError() {
    NotificationService.create({
      type: NotificationService.type.TOAST,
      subtype: NotificationService.subtype.ERROR,
      text: translate('android.toast_server_error'),
    });
  }

  useEffect(() => {
    messagingService.on(USER_LOGGED_OUT_ERROR, handleLogOutError);

    return () => {
      messagingService.off(USER_LOGGED_OUT_ERROR, handleLogOutError);
    };
  }, [messagingService]);

  useEffect(() => {
    if (location.search === '?broadcastStart=true') {
      setTimeout(() => onBroadcastButtonClick(), NO_OF_MILLISECONDS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeMobileMenu = useCallback(() => {
    drawerActions.toggle(MOBILE_MENU);
  }, [drawerActions]);

  const closeAccountMenu = useCallback(() => {
    drawerActions.toggle(ACCOUNT_MENU);
  }, [drawerActions]);

  return (
    <Fragment>
      <HeaderMain
        appActions={appActions}
        balance={balance}
        dispatch={dispatch}
        fingerprintData={fingerprintData}
        hideGuestMenu={isLoginPage}
        history={history}
        isMobileViewActive={isMobileViewActive}
        match={match}
        path={processedPath}
        setGuestBilllingModalProp={setGuestBilllingModalProp}
        showGuestBillingModal={showGuestBillingModal}
        showRegistrationModal={showRegistrationModal}
        user={user}
        userActions={userActions}
        onBroadcastButtonClick={onBroadcastButtonClick}
        onHeaderMainLoaded={onLoaded}
      />
      {isTabletViewActive && (
        <Fragment>
          <DrawerContent
            classes="darkGrey"
            opened={Boolean(drawer.get(MOBILE_MENU))}
            side="left"
            onClose={closeMobileMenu}
          >
            {isStudioOwner || isStudioAdmin ? (
              <MobileMenu
                availableLanguages={availableLanguages}
                dispatch={dispatch}
                history={history}
                mobileMenuItems={DRAWER_MENU_ITEMS}
                user={user}
                userActions={userActions}
                onListClick={closeMobileMenu}
              />
            ) : (
              <LeftMobileMenu
                appActions={appActions}
                availableLanguages={availableLanguages}
                computedMatch={computedMatch}
                dispatch={dispatch}
                drawerActions={drawerActions}
                history={history}
                user={user}
                userActions={userActions}
                onBroadcastButtonClick={onBroadcastButtonClick}
                onGenderChange={onGenderChange}
              />
            )}
          </DrawerContent>

          {isStudioOwner || isStudioAdmin ? (
            <StudioAccountMenuMobile
              history={history}
              isStudioAdmin={isStudioAdmin}
              isStudioOwner={isStudioOwner}
              opened={Boolean(drawer.get(ACCOUNT_MENU))}
              user={user}
              userActions={userActions}
              onClose={closeAccountMenu}
            />
          ) : (
            <AccountMenuMobile
              availableLanguages={availableLanguages}
              balance={balance}
              dispatch={dispatch}
              isPerformerHub={isPerformerHub}
              opened={Boolean(drawer.get(ACCOUNT_MENU))}
              path={processedPath}
              user={user}
              userActions={userActions}
              onClose={closeAccountMenu}
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};

Header.defaultProps = {
  appActions: {},
  availableLanguages: [],
  balance: 0,
  computedMatch: {},
  dispatch: '',
  drawer: {},
  drawerActions: {},
  history: {},
  isPerformerHub: false,
  isLoginPage: false,
  match: {},
  onGenderChange: '',
  processedPath: '',
  setGuestBilllingModalProp: '',
  showGuestBillingModal: false,
  showRegistrationModal: false,
  user: {},
  userActions: {},
  onLoaded: '',
};

Header.propTypes = {
  appActions: PropTypes.object,
  availableLanguages: PropTypes.array,
  balance: PropTypes.number,
  computedMatch: PropTypes.object,
  dispatch: PropTypes.func,
  drawer: PropTypes.object,
  drawerActions: PropTypes.object,
  history: PropTypes.object,
  isLoginPage: PropTypes.bool,
  isPerformerHub: PropTypes.bool,
  match: PropTypes.object,
  processedPath: PropTypes.string,
  setGuestBilllingModalProp: PropTypes.func,
  showGuestBillingModal: PropTypes.bool,
  showRegistrationModal: PropTypes.bool,
  user: PropTypes.object,
  userActions: PropTypes.object,
  onGenderChange: PropTypes.func,
  onLoaded: PropTypes.func,
};

export default observer(Header);
